import { supabase } from "./supabaseClient";

export async function runTaxAction(payload) {
  const { data, error } = await supabase.functions.invoke("tax-runner", {
    body: payload,
  });

  if (error) {
    throw new Error(error.message || "Failed to run tax action");
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data?.data ?? data;
}

export async function ensureCompanyTaxProfile(companyId) {
  return runTaxAction({
    action: "ensure_tax_profile",
    companyId,
  });
}

export async function generateTaxPeriods(companyId, year) {
  return runTaxAction({
    action: "generate_periods",
    companyId,
    year,
  });
}

export async function runVAT(companyId, taxPeriodId, mode = "standard") {
  return runTaxAction({
    action: "run_vat",
    companyId,
    taxPeriodId,
    mode,
  });
}

export async function runWHT(companyId, taxPeriodId, mode = "standard") {
  return runTaxAction({
    action: "run_wht",
    companyId,
    taxPeriodId,
    mode,
  });
}

export async function runCIT(companyId, taxPeriodId, mode = "standard") {
  return runTaxAction({
    action: "run_cit",
    companyId,
    taxPeriodId,
    mode,
  });
}

export async function runDevelopmentLevy(
  companyId,
  taxPeriodId,
  mode = "standard"
) {
  return runTaxAction({
    action: "run_dev_levy",
    companyId,
    taxPeriodId,
    mode,
  });
}

export async function createTaxFiling(computationId) {
  return runTaxAction({
    action: "create_filing",
    computationId,
  });
}

export async function getTaxPeriods(companyId, taxTypeCode = null) {
  let query = supabase
    .from("tax_periods")
    .select("*")
    .eq("company_id", companyId)
    .order("period_start", { ascending: false });

  if (taxTypeCode) {
    query = query.eq("tax_type_code", taxTypeCode);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || "Failed to fetch tax periods");
  }

  return data || [];
}

export async function getLatestTaxComputations(companyId, taxTypeCode = null) {
  let query = supabase
    .from("tax_computations")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (taxTypeCode) {
    query = query.eq("tax_type_code", taxTypeCode);
  }

  const { data, error } = await query.limit(20);

  if (error) {
    throw new Error(error.message || "Failed to fetch tax computations");
  }

  return data || [];
}

export async function getCompanyTaxProfile(companyId) {
  const { data, error } = await supabase
    .from("company_tax_profiles")
    .select("*")
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    throw new Error(error.message || "Failed to fetch company tax profile");
  }

  return data;
}

export async function saveCompanyTaxProfile(companyId, values) {
  const payload = {
    company_id: companyId,
    ...values,
  };

  const { data, error } = await supabase
    .from("company_tax_profiles")
    .upsert(payload, { onConflict: "company_id" })
    .select()
    .single();

  if (error) {
    throw new Error(error.message || "Failed to save company tax profile");
  }

  return data;
}

export async function getTaxFilings(companyId) {
  const { data, error } = await supabase
    .from("tax_filings")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(error.message || "Failed to fetch tax filings");
  }

  return data || [];
}

export async function getTaxComputationLines(taxComputationId) {
  const { data, error } = await supabase
    .from("tax_computation_lines")
    .select("*")
    .eq("tax_computation_id", taxComputationId)
    .order("created_at", { ascending: true });

  if (error) {
    throw new Error(error.message || "Failed to fetch tax computation lines");
  }

  return data || [];
}

export async function getTaxAdjustments(
  companyId,
  taxTypeCode = null,
  taxPeriodId = null
) {
  let query = supabase
    .from("tax_adjustments")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (taxTypeCode) {
    query = query.eq("tax_type_code", taxTypeCode);
  }

  if (taxPeriodId) {
    query = query.eq("tax_period_id", taxPeriodId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || "Failed to fetch tax adjustments");
  }

  return data || [];
}

export async function createTaxAdjustment(payload) {
  const { data, error } = await supabase
    .from("tax_adjustments")
    .insert(payload)
    .select()
    .single();

  if (error) {
    throw new Error(error.message || "Failed to create tax adjustment");
  }

  return data;
}

export async function updateTaxAdjustment(id, updates) {
  const { data, error } = await supabase
    .from("tax_adjustments")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message || "Failed to update tax adjustment");
  }

  return data;
}

export async function ensureTaxAccounts(companyId) {
  return runTaxAction({
    action: "ensure_tax_accounts",
    companyId,
  });
}

export async function getTaxAccounts(companyId, taxTypeCode = null) {
  let query = supabase
    .from("tax_accounts")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (taxTypeCode) {
    query = query.eq("tax_type_code", taxTypeCode);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || "Failed to fetch tax accounts");
  }

  return data || [];
}

export async function saveTaxAccountMapping(payload) {
  const { data, error } = await supabase
    .from("tax_accounts")
    .upsert(payload, {
      onConflict: "company_id,tax_type_code,tax_account_role,effective_from",
    })
    .select()
    .single();

  if (error) {
    throw new Error(error.message || "Failed to save tax account mapping");
  }

  return data;
}
